import { ArrowUpRight, ChevronRight, FileUp, Gauge, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Reveal } from "@/components/Motion";
import { DynoGraph } from "@/components/DynoGraph";
import { ToolCompatibility } from "@/components/ToolCompatibility";
import { API, PORTAL_URL, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";
import { ESTIMATE_NOTE, TUNING_CATEGORIES } from "@/lib/tuningCategories";
import { EstimatedBadge } from "@/pages/TuningCategoryIndex";

const STAGE_LABELS = { eco: "ECO", stage1: "Stage 1", stage2: "Stage 2", stage3: "Stage 3", e85: "E85", e85plus: "E85+" };

function Gain({ value, original, unit, testId }) {
  if (value == null || original == null) return null;
  const g = value - original;
  const pct = original ? Math.round((g / original) * 100) : null;
  if (g <= 0) return null;
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-700" data-testid={testId}>
      +{g} {unit}{pct != null ? ` · +${pct}%` : ""}
    </p>
  );
}

export default function TuningCategoryVehicle({ category }) {
  const cat = TUNING_CATEGORIES[category];
  const { make, model, generation, engine } = useParams();
  const [v, setV] = useState(null);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    setV(null);
    setMiss(false);
    axios
      .get(`${API}/tuning-database/categories/${category}/page/${make}/${model}/${generation}/${engine}`)
      .then((r) => setV(r.data))
      .catch(() => setMiss(true));
  }, [category, make, model, generation, engine]);

  const years = useMemo(() => {
    if (!v) return "";
    const m = (v.generation || "").match(/((?:19|20)\d{2})\s*[–—-]\s*((?:19|20)\d{2}|present)/i);
    if (m) return m[2].toLowerCase() === "present" ? `${m[1]}–present` : `${m[1]}–${m[2]}`;
    if (v.year_from && v.year_to) return `${v.year_from}–${v.year_to}`;
    if (v.year_from) return `${v.year_from}–`;
    return "";
  }, [v]);
  const name = v ? `${v.make} ${v.model}` : "";
  const stages = useMemo(() => (v?.display_stages || []).filter((s) => STAGE_LABELS[s.key]), [v]);
  const s1 = stages.find((s) => s.key === "stage1");
  const ecu = v?.ecus?.find((e) => (e.designation || "").trim());
  const isEstimated = v?.data_status === "estimated";

  // ---- SEO head ----
  useEffect(() => {
    if (!v) return;
    const s1b = s1?.bhp ?? v.stage1_bhp;
    const yearsTxt = years ? ` (${years})` : "";
    const titleTag = years || (v.generation || "").length <= 22 ? (years ? ` (${years})` : v.generation ? ` (${v.generation})` : "") : "";
    document.title = `${name} ${v.engine}${titleTag} ${cat.vehicleTitleTag}${s1b ? ` — ${v.original_bhp ?? "?"} to ${s1b} BHP` : ""} | Regal Tuning`;
    const descParts = [
      `${name} ${v.engine}${yearsTxt} tuning gains:`,
      v.original_bhp != null ? `factory ${v.original_bhp} BHP${v.original_nm != null ? ` / ${v.original_nm} Nm` : ""}` : null,
      s1b != null ? `Stage 1 ${isEstimated ? "(estimated) " : ""}takes it to ${s1b} BHP${s1?.nm != null ? ` / ${s1.nm} Nm` : ""}` : null,
      v.gain_bhp != null ? `(+${v.gain_bhp} BHP${v.gain_nm != null ? `, +${v.gain_nm} Nm` : ""})` : null,
      "ECU remapping from Regal Tuning — request your quote.",
    ].filter(Boolean);
    document.querySelector('meta[name="description"]')?.setAttribute("content", descParts.join(" ").slice(0, 300));
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_ORIGIN}${v.page_url}`;

    setJsonLd("tcv-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
        { "@type": "ListItem", position: 3, name: cat.name, item: `${SITE_ORIGIN}${cat.basePath}` },
        { "@type": "ListItem", position: 4, name: v.make, item: `${SITE_ORIGIN}${cat.basePath}/${v.page_url.split("/")[3]}` },
        { "@type": "ListItem", position: 5, name: `${name} ${v.engine}`, item: `${SITE_ORIGIN}${v.page_url}` },
      ],
    });
    setJsonLd("tcv-vehicle", {
      "@context": "https://schema.org",
      "@type": "Vehicle",
      name: `${name} ${v.engine}${yearsTxt}`,
      brand: { "@type": "Brand", name: v.make },
      model: v.model,
      ...(years ? { vehicleModelDate: String(v.year_from || "") } : {}),
      ...(v.fuel ? { fuelType: v.fuel } : {}),
      vehicleEngine: {
        "@type": "EngineSpecification",
        name: v.engine,
        ...(v.engine_size ? { engineDisplacement: { "@type": "QuantitativeValue", value: v.engine_size.replace(/[^\d.]/g, ""), unitCode: "CMQ" } } : {}),
        ...(v.original_bhp != null ? { enginePower: { "@type": "QuantitativeValue", value: v.original_bhp, unitCode: "BHP" } } : {}),
        ...(v.original_nm != null ? { torque: { "@type": "QuantitativeValue", value: v.original_nm, unitCode: "NU" } } : {}),
      },
      url: `${SITE_ORIGIN}${v.page_url}`,
    });
    if (s1?.bhp != null) {
      const faqs = [
        [`How much power does a Stage 1 remap add to the ${name} ${v.engine}?`,
         `The ${name} ${v.engine} produces ${v.original_bhp ?? "its factory"} BHP${v.original_nm != null ? ` and ${v.original_nm} Nm` : ""} as standard. A Regal Tuning Stage 1 calibration takes it to ${s1.bhp} BHP${s1.nm != null ? ` and ${s1.nm} Nm` : ""}${v.gain_bhp != null ? ` — a gain of ${v.gain_bhp} BHP${v.gain_nm != null ? ` and ${v.gain_nm} Nm` : ""}` : ""} with no hardware modifications required.${isEstimated ? " This figure is an estimate based on engine, ECU and comparable application data." : ""}`],
        [`Is a Stage 1 remap safe for the ${name} ${v.engine}?`,
         `Yes. Stage 1 calibrations are software-only and stay within the safe mechanical tolerances of the engine and drivetrain — calibrated conservatively for machinery that works long hours under sustained load.`],
        [`How do I get my ${name} ${v.engine} remapped?`,
         "Request a quote with your vehicle details or registration and our calibration team will confirm the exact figures for your vehicle. Existing customers upload their ECU file through the Regal Tuning file portal."],
      ];
      setJsonLd("tcv-faq", {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
      });
    }
    return () => {
      removeJsonLd("tcv-breadcrumb");
      removeJsonLd("tcv-vehicle");
      removeJsonLd("tcv-faq");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);

  if (miss) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="tcv-notfound">
        <div className="container-regal">
          <div className="border border-line bg-surface p-10 text-center">
            <Gauge size={28} className="mx-auto text-regal-amber" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Vehicle not found</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">We couldn't find that exact {cat.vehicleNoun}. Browse the {cat.name} database or ask our team — coverage grows continuously.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to={cat.basePath} className="inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="tcv-notfound-cat-link">
                Browse {cat.name}
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 border border-linebright px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="tcv-notfound-contact-link">
                Contact us
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!v) {
    return (
      <main className="pt-32 sm:pt-40 pb-24" data-testid="tcv-loading">
        <div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div>
      </main>
    );
  }

  const quoteUrl = `/contact?subject=${encodeURIComponent(`Remap quote — ${name} ${v.engine}${years ? ` (${years})` : ""}`)}&vehicle=${encodeURIComponent(`${name} ${v.engine}${years ? ` (${years})` : ""}`)}`;

  return (
    <main className="pt-32 sm:pt-40 pb-24" data-testid="tcv-page">
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-database" className="hover:text-regal-amber" data-testid="tcv-breadcrumb-db">Tuning Database</Link>
          <ChevronRight size={12} />
          <Link to={cat.basePath} className="hover:text-regal-amber" data-testid="tcv-breadcrumb-cat">{cat.name}</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{v.make} {v.model} {v.engine}</span>
        </nav>

        <p className="overline-label mb-4">{v.vehicle_type} · ECU Remapping</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="tcv-h1">
          {name} <span className="text-regal-amber">{v.engine}</span> Tuning{years ? <span className="text-neutral-400"> ({years})</span> : null}
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="tcv-intro">
          {`Factory specification and Stage 1 remap gains for the ${name} ${v.engine}${years ? ` (${years})` : ""}. `}
          {v.gain_bhp != null
            ? `A Stage 1 calibration adds ${v.gain_bhp} BHP${v.gain_nm != null ? ` and ${v.gain_nm} Nm` : ""} — software only, no hardware modifications required.`
            : "Stage 1 gains are confirmed in writing before any work begins."}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="tcv-specs">
          {[v.generation, years, v.engine_size, v.fuel, ecu ? `ECU: ${[ecu.maker, ecu.designation].filter(Boolean).join(" ")}` : null]
            .filter(Boolean)
            .map((chip) => (
              <span key={chip} className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid={`tcv-chip-${String(chip).slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}>
                {chip}
              </span>
            ))}
          {isEstimated && <EstimatedBadge testId="tcv-estimated-badge" />}
        </div>

        {/* PERFORMANCE TABLE */}
        <Reveal className="mt-10">
          <section className="border border-line" data-testid="tcv-table-section">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] bg-white text-left" data-testid="tcv-table">
                <thead>
                  <tr className="border-b border-line">
                    <th className="px-4 py-3 sm:px-6"><span className="sr-only">Measure</span></th>
                    <th className="px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500 sm:px-6">Original</th>
                    {stages.map((s) => (
                      <th key={s.key} className="bg-regal-amber px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-white sm:px-6" data-testid={`tcv-col-${s.key}`}>
                        {STAGE_LABELS[s.key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {["bhp", "nm"].map((unit) => (
                    <tr key={unit} className="border-b border-line last:border-0" data-testid={`tcv-row-${unit}`}>
                      <td className="px-4 py-4 sm:px-6">
                        <p className="font-display text-sm font-bold uppercase tracking-wide text-ink">{unit === "bhp" ? "Power" : "Torque"}</p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{unit === "bhp" ? "BHP" : "Nm"}</p>
                      </td>
                      <td className="px-4 py-4 text-center sm:px-6" data-testid={`tcv-stock-${unit}`}>
                        <p className="font-display text-2xl sm:text-3xl font-extrabold text-neutral-400">
                          {(unit === "bhp" ? v.original_bhp : v.original_nm) ?? "—"}
                          {unit === "nm" && v.original_nm_estimated && v.original_nm != null && <EstimatedBadge testId="tcv-stock-nm-est" />}
                        </p>
                      </td>
                      {stages.map((s) => (
                        <td key={s.key} className="bg-regal-amber/5 px-4 py-4 text-center sm:px-6" data-testid={`tcv-${s.key}-${unit}`}>
                          <p className="font-display text-2xl sm:text-3xl font-extrabold text-regal-amber">
                            {s[unit] ?? "—"}
                            {s.estimated && s[unit] != null && unit === "bhp" && <EstimatedBadge testId={`tcv-${s.key}-est-badge`} />}
                          </p>
                          <Gain value={s[unit]} original={unit === "bhp" ? v.original_bhp : v.original_nm} unit={unit === "bhp" ? "BHP" : "Nm"} testId={`tcv-gain-${s.key}-${unit}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {v.gain_bhp != null && (
              <div className="grid grid-cols-2 gap-px border-t border-line bg-line" data-testid="tcv-gain-summary">
                <div className="bg-surface p-5 text-center">
                  <p className="font-display text-3xl font-extrabold text-emerald-700" data-testid="tcv-gain-bhp">+{v.gain_bhp} BHP</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">Stage 1 power gain{isEstimated ? " (estimated)" : ""}</p>
                </div>
                <div className="bg-surface p-5 text-center">
                  <p className="font-display text-3xl font-extrabold text-emerald-700" data-testid="tcv-gain-nm">{v.gain_nm != null ? `+${v.gain_nm} Nm` : "—"}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">Stage 1 torque gain{isEstimated ? " (estimated)" : ""}</p>
                </div>
              </div>
            )}
            {isEstimated && (
              <p className="border-t border-line bg-white px-6 py-4 text-xs leading-relaxed text-neutral-500" data-testid="tcv-estimate-note">
                {ESTIMATE_NOTE}
              </p>
            )}
          </section>
        </Reveal>

        {/* DYNO GRAPH */}
        <Reveal>
          <DynoGraph vehicle={v} testPrefix="tcv" />
        </Reveal>

        {/* TOOL COMPATIBILITY */}
        <Reveal>
          <ToolCompatibility items={v.tool_compat} testPrefix="tcv" />
        </Reveal>

        {/* CTAs */}
        <div className="mt-8 flex flex-col gap-3 border border-line bg-white p-6 sm:flex-row sm:items-center sm:p-8" data-testid="tcv-ctas">
          <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors" data-testid="tcv-cta-portal">
            <FileUp size={15} /> Upload a File
          </a>
          <Link to={quoteUrl} className="inline-flex items-center justify-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="tcv-cta-quote">
            Request a Quote <ArrowUpRight size={15} />
          </Link>
          <p className="text-xs text-neutral-500 sm:ml-auto sm:max-w-[240px]">
            Existing customers upload their ECU file via the <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-regal-amber underline-offset-4 hover:underline">Regal Tuning File Portal</a>. New to Regal? Request a quote first.
          </p>
        </div>

        {/* BOTTOM CTA */}
        <div className="mt-16 flex flex-col items-start justify-between gap-6 border border-line bg-surface p-8 sm:flex-row sm:items-center" data-testid="tcv-bottom-cta">
          <div>
            <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber"><ShieldCheck size={14} /> 20+ years of ECU calibration</p>
            <h2 className="mt-2 font-display text-xl sm:text-2xl font-bold uppercase tracking-tight text-ink">{`Ready to unlock your ${name}'s real performance?`}</h2>
            <p className="mt-2 max-w-xl text-sm text-neutral-400">Tell us your {cat.vehicleNoun} and registration — we'll confirm support and the exact expected gains for your engine before any work begins.</p>
          </div>
          <Link to={quoteUrl} className="inline-flex shrink-0 items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="tcv-bottom-cta-button">
            Request a Quote <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </main>
  );
}
